import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { DemoAccountType } from '../../../common/model/dto/demo-account-types.enum';
import {
  Alert,
  AlertTitle,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import styled from 'styled-components';
import OvSelect from '../atoms/OvSelect';
import breakpoints from '../../../design-system/breakpoints';
import Variables from '../../../design-system/variables';
import OvButton from '../atoms/OvButton';
import Colours from '../../../design-system/colours';
import { CreateDemoAccountRequest } from '../../../common/model/dto/create-demo-account-request';
import * as Yup from 'yup';
import OvPasswordField from './OvPasswordField';
import { HealthGoalType } from '../../../common/model/dto/health-goal-type.enum';

const DEMO_ACCOUNT_HEALTH_GOALS: Record<DemoAccountType, HealthGoalType> = {
  [DemoAccountType.NATURAL_CYCLE_MONITORING_AND_TIMED_INTERCOURSE]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.TIMED_INTERCOURSE_CYCLE_WITH_CLOMID]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.TIMED_INTERCOURSE_CYCLE_WITH_LETROZOLE]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.NATURAL_CYCLE_IUI_WITH_CLOMID_OR_LETROZOLE]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.NATURAL_OR_MODIFIED_NATURAL_FROZEN_EMBRYO_TRANSFER]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.LUPRON_TRIGGER_SHOT_MONITORING]:
    HealthGoalType.TRYING_TO_CONCEIVE,
  [DemoAccountType.PERIMENOPAUSE_REGULAR_CYCLES]:
    HealthGoalType.PERIMENOPAUSE_TRACKING,
  [DemoAccountType.PERIMENOPAUSE_NO_CYCLES]:
    HealthGoalType.PERIMENOPAUSE_TRACKING,
  [DemoAccountType.PERIMENOPAUSE_HRT]: HealthGoalType.PERIMENOPAUSE_TRACKING,
  [DemoAccountType.HORMONE_OPTIMIZATION_SLEEP_DISTURBANCES]:
    HealthGoalType.HORMONE_OPTIMIZATION,
  [DemoAccountType.HORMONE_OPTIMIZATION_MIGRAINES]:
    HealthGoalType.HORMONE_OPTIMIZATION,
};

const OvCreateDemoAccountDialog: FC<{
  isOpen: boolean;
  onCancel: () => void;
  onSave: (request: CreateDemoAccountRequest) => void;
  title: string;
  enableCreatePassword: boolean;
}> = ({ isOpen, onCancel, onSave, title, enableCreatePassword }) => {
  const { t } = useTranslation();
  const [showCreatePasswordSection, setShowCreatePasswordSection] =
    useState(false);
  const [validation, setValidation] = useState(Yup.object().shape({}));
  const demoAccountTypeList: { label: string; value: DemoAccountType }[] = [
    {
      label: t(
        'common.demoAccounts.types.naturalCycleMonitoringAndTimeIntercourse'
      ),
      value: DemoAccountType.NATURAL_CYCLE_MONITORING_AND_TIMED_INTERCOURSE,
    },
    {
      label: t('common.demoAccounts.types.timedIntercourseCycleWithClomid'),
      value: DemoAccountType.TIMED_INTERCOURSE_CYCLE_WITH_CLOMID,
    },
    {
      label: t('common.demoAccounts.types.timedIntercourseCycleWithLetrozole'),
      value: DemoAccountType.TIMED_INTERCOURSE_CYCLE_WITH_LETROZOLE,
    },
    {
      label: t(
        'common.demoAccounts.types.naturalCycleIuiWithClomidOrLetrozole'
      ),
      value: DemoAccountType.NATURAL_CYCLE_IUI_WITH_CLOMID_OR_LETROZOLE,
    },
    {
      label: t(
        'common.demoAccounts.types.naturalOrModifiedNaturalFrozenEmbryoTransfer'
      ),
      value: DemoAccountType.NATURAL_OR_MODIFIED_NATURAL_FROZEN_EMBRYO_TRANSFER,
    },
    {
      label: t('common.demoAccounts.types.lupronTriggershotMonitoring'),
      value: DemoAccountType.LUPRON_TRIGGER_SHOT_MONITORING,
    },
    {
      label: t('common.demoAccounts.types.perimenopauseRegularCycles'),
      value: DemoAccountType.PERIMENOPAUSE_REGULAR_CYCLES,
    },
    {
      label: t('common.demoAccounts.types.perimenopauseNoCycles'),
      value: DemoAccountType.PERIMENOPAUSE_NO_CYCLES,
    },
    {
      label: t('common.demoAccounts.types.perimenopauseHrt'),
      value: DemoAccountType.PERIMENOPAUSE_HRT,
    },
    {
      label: t(
        'common.demoAccounts.types.hormoneOptimizationSleepDisturbances'
      ),
      value: DemoAccountType.HORMONE_OPTIMIZATION_SLEEP_DISTURBANCES,
    },
    {
      label: t('common.demoAccounts.types.hormoneOptimizationMigraines'),
      value: DemoAccountType.HORMONE_OPTIMIZATION_MIGRAINES,
    },
  ];
  const [selectedDemoAccountTypeValue, setSelectedDemoAccountTypeValue] =
    useState<DemoAccountType>(demoAccountTypeList[0].value);
  const formik = useFormik<CreateDemoAccountRequest>({
    initialValues: {
      demo_account_type:
        DemoAccountType.NATURAL_CYCLE_MONITORING_AND_TIMED_INTERCOURSE,
      password: undefined,
    },
    onSubmit: (createDemoAccountRequest) => {
      onSave({
        demo_account_type: createDemoAccountRequest.demo_account_type,
        health_goal:
          DEMO_ACCOUNT_HEALTH_GOALS[createDemoAccountRequest.demo_account_type],
        password: showCreatePasswordSection
          ? createDemoAccountRequest.password
          : undefined,
      });
    },
    validationSchema: validation,
  });

  useEffect(() => {
    if (isOpen) {
      formik.resetForm();
      setSelectedDemoAccountTypeValue(demoAccountTypeList[0].value);
      setShowCreatePasswordSection(false);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (showCreatePasswordSection) {
      setValidation(
        Yup.object().shape({
          password: Yup.string()
            .min(8, 'Password must be at least 8 characters long.')
            .max(128, 'Password cannot exceed 128 characters.')
            .required(),
        })
      );
    } else {
      setValidation(Yup.object().shape({}));
    }
  }, [showCreatePasswordSection]);

  const handleDemoAccountTypeValueChange = (event: any, newValue?: any) => {
    const value = event?.target ? event.target.value : newValue;
    setSelectedDemoAccountTypeValue(value);
    formik.setFieldValue('demo_account_type', value);
  };

  return (
    <Dialog open={isOpen} onClose={onCancel}>
      <StyledDialogTitle>{title}</StyledDialogTitle>
      <StyledAlert severity="warning">
        <AlertTitle>
          {t('common.demoAccounts.demoAccountWarningTitle')}
        </AlertTitle>
        {t('common.demoAccounts.demoAccountWarningDesc')}
      </StyledAlert>
      <form onSubmit={formik.handleSubmit}>
        <StyledDialogContent>
          <StyledSelectTitle>
            {t('common.demoAccounts.chooseDemoAccountType')}:
          </StyledSelectTitle>
          <StyledOvSelect
            value={selectedDemoAccountTypeValue}
            onChange={handleDemoAccountTypeValueChange}
          >
            {demoAccountTypeList &&
              demoAccountTypeList.map((type) => (
                <StyledMenuItem key={type.value} value={type.value}>
                  {type.label}
                </StyledMenuItem>
              ))}
          </StyledOvSelect>
          {enableCreatePassword && (
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={showCreatePasswordSection}
                  onChange={() =>
                    setShowCreatePasswordSection(!showCreatePasswordSection)
                  }
                  name="showCreatePasswordSection"
                />
              }
              label={t('common.demoAccounts.createPasswordForDemoUser')}
            />
          )}

          {showCreatePasswordSection && enableCreatePassword && (
            <>
              <StyledDialogDescription>
                {t('userDialogs.createPasswordForDemoAccountDescription')}
              </StyledDialogDescription>
              <OvPasswordField
                style={{
                  marginTop: '1rem',
                  marginBottom: '1rem',
                  justifySelf: 'center',
                }}
                name="password"
                fullWidth
                label={t('login.passwordLabel') + '*'}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && !!formik.errors.password}
                helperText={
                  formik.errors.password &&
                  formik.touched.password &&
                  formik.errors.password
                }
              />
            </>
          )}
          <StyledDialogActions>
            <StyledLightOvButton onClick={onCancel}>
              {t('common.actions.cancel')}
            </StyledLightOvButton>
            <StyledOvButton type="submit">
              {t('common.actions.create')}
            </StyledOvButton>
          </StyledDialogActions>
        </StyledDialogContent>
      </form>
    </Dialog>
  );
};

export default OvCreateDemoAccountDialog;

const StyledDialogTitle = styled(DialogTitle)`
  && {
    text-align: center;
    font-weight: bold;
    text-transform: none;
  }
`;

const StyledSelectTitle = styled.p`
  font-weight: bold;
  padding: 0;
  margin: 0;
  color: ${Colours.OV_BASE};
`;

const StyledOvSelect = styled(OvSelect)`
  && {
    width: 100%;
    max-width: 100%;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 100%;
    min-width: 31.25rem !important;
    padding: 0.5rem 1.5rem !important;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;

    @media (min-width: ${breakpoints.sm}) {
      width: 34rem;
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  && {
    &.MuiButtonBase-root {
      display: flex;
      justify-content: flex-start;
      padding: 0.375rem 1rem;
      font-size: ${Variables.fontSizes.MEDIUM};
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  && {
    margin-top: 2rem;
    padding: 0 0 0.5rem 1.5rem;
  }
`;

const StyledLightOvButton = styled(OvButton)`
  && {
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    padding: 0 1rem;
    background-color: ${Colours.OV_WHITE};
    border: 1px solid rgba(1, 39, 70, 0.5);
    color: ${Colours.OV_BASE};
    font-weight: bold;
    text-transform: none;
    &:hover {
      border: 1px solid ${Colours.OV_BASE};
      background-color: rgba(1, 39, 70, 0.04);
    }
  }
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem !important;
    align-self: flex-end;
  }
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0.25rem 0.5rem 0.25rem;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};
    max-width: 32rem;
    align-self: center;
    text-align: justify;

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  && {
    padding-bottom: 0.5rem;
    width: 100%;
    margin-left: 0;
  }
`;

const StyledDialogDescription = styled(DialogContentText)`
  && {
    max-width: 30rem;
    text-align: left;
    margin-bottom: 1rem;
  }
`;
